/**
 * 메이저 아르카나 22장 해설 — 카드 도감 상세 페이지(/cards/:slug) 본문.
 *
 * 왜 22장만인가:
 *  마이너 56장은 "슈트 × 랭크" 조합으로 생성되는 구조(cards.ts)라, 글로 풀면
 *  같은 뼈대에 단어만 바뀐 문서가 56개 나온다. 검색엔진은 이런 양산형 문서를
 *  걸러내고, 얇은 페이지는 사이트 전체 평가를 떨어뜨린다.
 *  메이저 22장은 카드마다 고유한 이름과 서사가 있어 실제로 다른 글이 나오고,
 *  검색 수요도 여기에 몰려 있다. 마이너는 키워드 도감으로만 둔다.
 *
 * `id`는 카드 id(0~21)와 같다.
 */
export interface MajorMeaning {
  id: number;
  /** 카드 그림과 핵심 서사 */
  story: string;
  /** 정방향 해석 */
  upright: string;
  /** 역방향 해석 */
  reversed: string;
  /** 연애에서 */
  inLove: string;
  /** 일·학업에서 */
  inWork: string;
  /** 이 카드가 건네는 한 마디 */
  advice: string;
}

export const MAJOR_MEANINGS: readonly MajorMeaning[] = [
  {
    id: 0,
    story:
      '한 젊은이가 봇짐 하나만 메고 절벽 끝을 향해 걸어갑니다. 발밑은 보지 않고 하늘을 올려다보고 있어요. 위험해 보이지만, 아직 아무것도 정해지지 않은 사람만이 가질 수 있는 표정입니다.',
    upright:
      '무언가를 새로 시작하려는 순간에 나오는 카드예요. 경험이 없다는 게 약점이 아니라, 선입견 없이 뛰어들 수 있는 힘이 되는 시기입니다. 계산이 서지 않아도 마음이 끌리는 쪽으로 한 발 내딛는 것이 이 카드의 조언이에요. 결과를 다 알고 시작할 수 있는 일은 원래 없다는 걸 일러줍니다.',
    reversed:
      '용기가 무모함으로 기울었을 때 나와요. 확인해야 할 것을 건너뛰었거나, 반대로 겁이 나서 첫발을 못 떼고 제자리를 맴돌고 있을 수도 있습니다. 어느 쪽이든 "지금 딛는 자리가 어디인지" 한 번 내려다보라는 신호예요.',
    inLove:
      '아직 이름 붙이지 않은 관계, 막 시작된 설렘을 말합니다. 조건을 따지기보다 마음이 먼저 움직이는 시기예요.',
    inWork:
      '새 일, 새 분야, 새 배움이 열립니다. 잘 모르는 영역이라도 지금은 뛰어들어 배우는 게 빠릅니다.',
    advice: '완벽한 준비를 기다리지 말고, 오늘 할 수 있는 첫 걸음을 정해보세요.',
  },
  {
    id: 1,
    story:
      '탁자 위에 네 슈트의 도구가 모두 놓여 있고, 마법사는 한 손을 하늘로 다른 손을 땅으로 향합니다. 필요한 것은 이미 다 갖췄고, 이제 쓰기만 하면 된다는 자세입니다.',
    upright:
      '가진 것을 실제로 써먹을 때가 왔다는 카드예요. 재능이나 자원이 부족한 게 아니라, 그것을 하나로 모아 실행에 옮기는 일이 남았습니다. 생각만 하던 것을 말로 꺼내고, 말한 것을 손으로 만드는 단계로 넘어가라는 뜻이에요. 집중력과 의지가 결과를 가르는 시기입니다.',
    reversed:
      '능력은 있는데 방향이 흩어졌을 때 나옵니다. 재능을 엉뚱한 데 쓰고 있거나, 스스로를 과소평가해 시작조차 안 하고 있을 수 있어요. 때로는 말과 실제가 다른 상황, 보여주기에 치우친 태도를 짚기도 합니다.',
    inLove:
      '먼저 다가가고 표현하면 통하는 때예요. 마음을 짐작하게 두지 말고 분명한 말로 전해보세요.',
    inWork:
      '기획을 실행으로 옮기기 좋은 시기입니다. 자신의 강점을 전면에 내세울 자리라면 더욱 그렇습니다.',
    advice: '이미 가진 것을 세어보세요. 새로 구할 게 아니라 쓸 차례입니다.',
  },
  {
    id: 2,
    story:
      '두 기둥 사이에 앉은 여사제 뒤로 장막이 드리워져 있습니다. 그 너머에 무엇이 있는지는 보이지 않아요. 말하지 않고 지켜보는 사람의 자리입니다.',
    upright:
      '아직 드러나지 않은 것이 있다는 카드예요. 논리로 따져 답이 나오지 않을 때, 이미 마음 한구석에서 알고 있던 감각을 믿으라고 말합니다. 서두르지 말고 조용히 지켜보는 편이 유리한 시기예요. 상대나 상황에 대해 아직 모르는 사정이 있을 수 있으니, 성급한 결론을 미뤄두는 게 좋습니다.',
    reversed:
      '직감을 무시하고 겉으로 보이는 것만 믿었을 때 나와요. 반대로 속내를 너무 감춰 오해가 쌓였거나, 알아야 할 정보가 가려져 있는 상황일 수도 있습니다. 침묵이 길어지면 신뢰가 아니라 거리감이 됩니다.',
    inLove:
      '말로 다 표현되지 않은 마음이 오가는 중이에요. 상대의 침묵을 무관심으로 단정하지 마세요.',
    inWork:
      '아직 공개되지 않은 변화가 진행 중일 수 있습니다. 관찰하고 기록해 두는 것이 지금의 일입니다.',
    advice: '답을 밖에서 찾기 전에, 이미 알고 있던 느낌을 먼저 꺼내보세요.',
  },
  {
    id: 3,
    story:
      '풍요로운 들판에 앉은 여황제 주위로 밀이 익어갑니다. 무언가를 억지로 만들어내는 대신, 자라도록 돌보는 사람의 자리예요.',
    upright:
      '무언가가 자라고 열매를 맺는 시기입니다. 새로 시작하는 힘보다 이미 시작한 것을 키우고 돌보는 힘이 중요해요. 관계든 일이든 정성을 들인 만큼 돌아오는 때이니, 조급하게 결과를 뽑아내려 하지 말고 자라는 속도를 존중하는 게 좋습니다. 감각적인 즐거움과 편안함을 누려도 좋은 시기이기도 합니다.',
    reversed:
      '돌봄이 지나쳐 간섭이 되었을 때 나와요. 상대를 위한다는 마음이 상대의 몫까지 대신하는 형태가 되었을 수 있습니다. 반대로 스스로를 돌보지 못해 창의력과 여유가 말라버린 상태를 뜻하기도 합니다.',
    inLove: '따뜻하고 안정적인 관계를 말합니다. 다만 애정이 통제로 변하지 않는지 살펴보세요.',
    inWork: '꾸준히 해온 일이 성과로 드러납니다. 새 판을 벌이기보다 지금 것을 여물게 할 때예요.',
    advice: '자라는 데는 시간이 듭니다. 오늘은 재촉 대신 물을 주세요.',
  },
  {
    id: 4,
    story:
      '돌로 된 옥좌에 앉은 황제 뒤로 메마른 산이 서 있습니다. 부드럽지는 않지만 흔들리지 않는 자리, 질서를 세우는 사람의 카드입니다.',
    upright:
      '구조와 규칙이 필요한 때예요. 흩어진 상황에 기준을 세우고 경계를 그으면 오히려 편해집니다. 감정보다 원칙으로 판단하는 편이 유리하고, 책임을 지는 위치에 서게 되기도 해요. 지켜야 할 선을 분명히 하는 것이 이 카드가 말하는 안정입니다.',
    reversed:
      '원칙이 고집으로 굳었을 때 나옵니다. 융통성 없이 밀어붙이거나, 반대로 통제력을 잃고 상황에 끌려다니는 상태예요. 권위 있는 사람과의 마찰을 뜻하기도 합니다.',
    inLove: '안정감을 주는 관계이지만, 한쪽이 결정을 독점하고 있지 않은지 봐야 해요.',
    inWork: '체계를 잡고 규칙을 정하기 좋은 시기입니다. 책임 범위를 명확히 하면 일이 풀려요.',
    advice: '지키고 싶은 것이 있다면, 먼저 그 경계를 말로 정해두세요.',
  },
  {
    id: 5,
    story:
      '교황이 두 사람에게 가르침을 전합니다. 개인의 깨달음이 아니라 오래 이어져 온 방식, 여럿이 공유하는 기준에 관한 카드예요.',
    upright:
      '이미 검증된 방식을 따르는 편이 나은 때입니다. 혼자 새 길을 내기보다 배우고, 조언을 구하고, 소속된 곳의 규칙 안에서 움직이는 게 유리해요. 결혼·계약·입사처럼 사회적으로 인정받는 형태를 갖추는 일과도 잘 어울립니다. 스승이나 멘토가 나타나기도 합니다.',
    reversed:
      '틀이 답답하게 느껴질 때 나와요. 관습에 반발하거나, 반대로 남들이 다 하니까 따라가면서 정작 자신의 기준을 잃었을 수 있습니다. 형식만 남고 의미가 빠진 상황을 짚기도 해요.',
    inLove: '관계를 공식화하는 흐름이에요. 주변에 알리거나 약속의 형태를 갖추는 이야기가 나옵니다.',
    inWork:
      '조직의 방식을 익히고 따르는 것이 지금은 빠른 길입니다. 자격이나 배움도 좋은 선택이에요.',
    advice: '먼저 걸어본 사람에게 물어보세요. 지금은 배우는 쪽이 이깁니다.',
  },
  {
    id: 6,
    story:
      '두 사람이 마주 서 있고 그 위로 천사가 손을 뻗습니다. 사랑의 카드로 알려져 있지만, 본래는 무엇을 택할 것인가에 대한 카드입니다.',
    upright:
      '마음이 통하고 관계가 깊어지는 시기예요. 동시에 중요한 선택 앞에 서 있다는 뜻이기도 합니다. 이때의 기준은 손익이 아니라 "무엇이 나다운가"예요. 진심을 걸고 고른 선택은 시간이 지나도 후회가 적다는 것을 이 카드가 말합니다. 서로의 차이를 인정하면서 조화를 이루는 관계를 가리키기도 해요.',
    reversed:
      '마음과 조건이 어긋났을 때 나와요. 결정을 미루며 양쪽을 다 붙잡고 있거나, 유혹에 흔들리는 상황일 수 있습니다. 가치관 차이가 드러나 관계가 삐걱대기도 해요.',
    inLove: '관계의 방향을 정할 때가 왔다는 신호예요. 애매하게 두는 것이 가장 위험합니다.',
    inWork: '함께할 사람, 혹은 갈 길을 고르는 국면입니다. 협업 상대와의 궁합이 성패를 가릅니다.',
    advice: '둘 다 갖는 선택은 없습니다. 무엇을 내려놓을지부터 정하세요.',
  },
  {
    id: 7,
    story:
      '검은 말과 흰 말이 각기 다른 곳을 보는데, 전차는 앞으로 나아갑니다. 고삐가 아니라 의지로 끌고 가는 장면이에요.',
    upright:
      '상반된 것들을 붙들고서라도 밀고 나가는 힘을 말합니다. 지금은 흔들리는 마음을 정리한 뒤에 움직이는 게 아니라, 움직이면서 정리하는 시기예요. 목표를 분명히 하고 속도를 내면 성과가 따라옵니다. 자기 통제가 곧 추진력이 되는 카드입니다.',
    reversed:
      '방향 없이 속도만 냈을 때 나와요. 조급함에 밀려 무리하거나, 반대로 통제력을 잃고 멈춰 선 상태입니다. 여러 일을 동시에 붙들다 어느 것도 끝내지 못하는 상황을 뜻하기도 해요.',
    inLove: '적극적으로 다가가면 통합니다. 다만 상대의 속도를 무시한 밀어붙임은 역효과예요.',
    inWork: '경쟁에서 앞서 나갈 수 있는 시기입니다. 목표 하나에 자원을 모으세요.',
    advice: '가장 중요한 하나를 정하고, 나머지는 잠시 내려두세요.',
  },
  {
    id: 8,
    story:
      '여인이 사자의 입을 맨손으로 부드럽게 감쌉니다. 힘으로 누르는 것이 아니라 길들이는 장면이에요.',
    upright:
      '거친 것을 부드럽게 다루는 힘에 대한 카드입니다. 화나 두려움 같은 감정을 억누르는 대신 인정하고 함께 가는 방식이 통해요. 당장 이기려 하기보다 오래 버티는 쪽이 결국 이깁니다. 스스로에 대한 믿음이 흔들릴 때 필요한 것은 더 센 채찍이 아니라 인내라는 것을 말해줍니다.',
    reversed:
      '자신감이 무너졌을 때 나와요. 감정에 휘둘려 후회할 말을 했거나, 참아야 한다는 생각에 지나치게 눌러 지쳤을 수도 있습니다. 어느 쪽이든 스스로를 대하는 방식이 너무 거칠어졌다는 신호예요.',
    inLove: '조급하게 확답을 받아내려 하지 않는 편이 좋아요. 기다려주는 태도가 관계를 지킵니다.',
    inWork: '단기 성과보다 꾸준함이 평가받는 시기입니다. 어려운 사람도 부드럽게 대하면 풀립니다.',
    advice: '이기려 하지 말고 견디세요. 지금은 그것이 더 센 힘입니다.',
  },
  {
    id: 9,
    story:
      '노인이 등불 하나를 들고 어두운 산길에 서 있습니다. 등불은 멀리까지 비추지 못하고, 딱 한 걸음 앞만 밝힙니다.',
    upright:
      '혼자 있는 시간이 필요하다는 카드예요. 답을 밖에서 구하기보다 스스로 정리해야 하는 시기이고, 그러려면 소란에서 잠시 빠져나와야 합니다. 속도를 늦추는 것이 후퇴가 아니라 방향을 찾는 과정이에요. 이 시기를 지나면 남에게 길을 알려줄 수 있는 사람이 되기도 합니다.',
    reversed:
      '고독이 고립으로 굳었을 때 나옵니다. 도움을 청해야 할 때 혼자 버티고 있거나, 현실을 피해 숨어 있는 상태일 수 있어요. 반대로 생각할 시간이 절실한데 계속 끌려다니고 있는 상황이기도 합니다.',
    inLove: '거리를 두고 마음을 정리하는 시기예요. 상대의 혼자만의 시간을 이별 신호로 읽지 마세요.',
    inWork: '방향 점검이 필요한 때입니다. 새 일을 벌이기보다 지금 하는 일의 의미를 다시 보세요.',
    advice: '한 걸음 앞만 밝히면 충분합니다. 전부를 보려 하지 마세요.',
  },
  {
    id: 10,
    story:
      '수레바퀴가 돌고, 위에 있던 것이 아래로 아래에 있던 것이 위로 갑니다. 멈춰 있는 것은 아무것도 없다는 그림이에요.',
    upright:
      '흐름이 바뀌는 전환점을 말합니다. 내 힘으로 만든 변화가 아니라 때가 되어 찾아온 변화라, 붙잡기보다 올라타는 편이 유리해요. 오래 막혀 있던 일이 갑자기 풀리기도 합니다. 지금의 좋은 흐름도 영원하지 않다는 것을 함께 일러주는 카드예요.',
    reversed:
      '변화에 저항할 때 나옵니다. 흐름이 바뀌었는데 예전 방식을 고수하고 있거나, 운이 따르지 않는다고 느끼는 시기예요. 예상 밖의 지연이 생기기도 합니다.',
    inLove: '관계의 국면이 바뀝니다. 우연한 만남이나 재회의 흐름이 생기기도 해요.',
    inWork: '기회가 예고 없이 옵니다. 준비해 둔 사람이 잡는 종류의 기회예요.',
    advice: '지금 상황이 계속될 거라 가정하지 마세요. 좋을 때도, 나쁠 때도요.',
  },
  {
    id: 11,
    story:
      '한 손에 저울, 다른 손에 검을 든 인물이 정면을 바라봅니다. 감정을 섞지 않고 있는 그대로 재는 자리입니다.',
    upright:
      '공정함과 균형에 관한 카드예요. 지금까지의 선택이 그대로 결과로 돌아오는 시기이고, 유리하게 꾸미기보다 사실대로 마주하는 편이 낫습니다. 계약이나 합의처럼 명확히 매듭지어야 할 일에 잘 맞아요. 책임질 것은 책임지고 나면 오히려 가벼워집니다.',
    reversed:
      '균형이 무너졌을 때 나옵니다. 한쪽이 일방적으로 손해를 보고 있거나, 책임을 미루고 있는 상황이에요. 편향된 판단이나 미뤄둔 정산이 문제를 키우고 있을 수 있습니다.',
    inLove: '주고받는 것이 한쪽으로 기울지 않았는지 살펴볼 때예요. 솔직한 대화가 필요합니다.',
    inWork: '평가·계약·정산이 이루어집니다. 문서와 조건을 꼼꼼히 확인하세요.',
    advice: '유리한 쪽이 아니라 사실인 쪽을 고르세요.',
  },
  {
    id: 12,
    story:
      '한 사람이 나무에 거꾸로 매달려 있는데, 표정은 평온합니다. 스스로 그 자세를 택한 사람의 얼굴이에요.',
    upright:
      '지금은 움직일 때가 아니라는 카드입니다. 답답하게 느껴지는 정지 상태가 실은 관점을 뒤집어 보는 시간이에요. 애써 상황을 바꾸려 하기보다 받아들이고 기다릴 때 길이 보입니다. 무언가를 얻기 위해 다른 것을 내려놓아야 하는 국면이기도 해요.',
    reversed:
      '기다림이 의미를 잃었을 때 나옵니다. 헛된 희생을 계속하고 있거나, 미련 때문에 놓아야 할 것을 붙들고 있는 상태예요. 결단을 미루는 것 자체가 손해가 되고 있습니다.',
    inLove: '상대의 입장에서 다시 볼 필요가 있어요. 지금은 밀어붙이면 오히려 멀어집니다.',
    inWork: '진행이 멈춘 것처럼 느껴지지만 낭비는 아닙니다. 방식 자체를 바꿔볼 시기예요.',
    advice: '지금 안 되는 일을 붙들지 말고, 보는 각도를 바꿔보세요.',
  },
  {
    id: 13,
    story:
      '이름 때문에 가장 무섭게 여겨지는 카드지만, 그림 속 배경에는 해가 떠오릅니다. 하나가 끝나야 다음이 시작된다는 그림이에요.',
    upright:
      '무언가가 확실히 끝나는 시기입니다. 되돌리려 애쓰기보다 마무리를 잘 짓는 편이 나아요. 관계든 일이든 이미 수명을 다한 것을 붙들고 있으면 다음이 오지 못합니다. 이 카드는 상실을 말하는 동시에, 그 자리에 무엇이 들어올지를 묻습니다. 실제 죽음을 뜻하지 않아요.',
    reversed:
      '끝내야 할 것을 끝내지 못할 때 나옵니다. 변화를 거부하거나 미련이 길어져 정체된 상태예요. 이미 끝난 일을 마음속에서만 계속 이어가고 있을 수 있습니다.',
    inLove: '관계의 한 장이 닫힙니다. 형태가 바뀌거나, 정리를 통해 다시 시작되기도 해요.',
    inWork: '그만두거나 넘기는 결정이 필요합니다. 정리하고 나면 자리가 생겨요.',
    advice: '끝난 것을 끝났다고 인정하는 데서 다음이 시작됩니다.',
  },
  {
    id: 14,
    story:
      '천사가 두 잔 사이로 물을 옮깁니다. 한쪽 발은 물에, 한쪽 발은 땅에 두고 어느 쪽으로도 기울지 않아요.',
    upright:
      '섞고 조율하는 시기입니다. 양극단 중 하나를 고르는 대신 중간 지점을 찾는 편이 유리해요. 서두르지 않고 조금씩 맞춰가는 방식이 통하고, 지쳤던 마음이나 관계가 회복되기도 합니다. 절제는 참는 것이 아니라 알맞은 양을 아는 것이라는 걸 말해줍니다.',
    reversed:
      '균형이 깨졌을 때 나와요. 한쪽으로 치우쳐 과하게 쏟아붓고 있거나, 조급함에 과정을 건너뛰고 있습니다. 무리한 상태가 이어져 소진되기 직전일 수도 있어요.',
    inLove: '서로 다른 속도를 맞춰가는 시기입니다. 인내가 관계를 단단하게 만들어요.',
    inWork: '무리한 일정보다 지속 가능한 속도가 중요합니다. 협업에서는 중재자 역할이 어울려요.',
    advice: '조금 덜 하는 것이, 지금은 더 잘하는 방법입니다.',
  },
  {
    id: 15,
    story:
      '두 사람이 사슬에 묶여 있는데, 목에 걸린 고리는 헐겁습니다. 마음만 먹으면 벗을 수 있지만 그러지 않고 있어요.',
    upright:
      '무언가에 매여 있는 상태를 말합니다. 습관, 관계, 물욕, 혹은 벗어나지 못하는 생각일 수도 있어요. 핵심은 그 속박이 대체로 스스로 선택한 것이라는 점입니다. 당장은 편하기 때문에 붙들고 있는 것이 무엇인지 들여다보라는 카드예요. 강한 끌림이나 집착을 가리키기도 합니다.',
    reversed:
      '묶여 있던 것에서 벗어나는 흐름이에요. 문제를 자각하고 끊어내기 시작했거나, 관계를 정리하는 국면입니다. 역방향이 오히려 해방을 뜻하는 드문 카드 중 하나예요.',
    inLove: '끌리지만 건강하지 않은 관계일 수 있어요. 무엇 때문에 놓지 못하는지 살펴보세요.',
    inWork: '조건 때문에 버티고 있는 상황을 가리킵니다. 익숙함이 이유의 전부는 아닌지 물어보세요.',
    advice: '벗어날 수 없다고 느낀다면, 정말 그런지부터 확인해 보세요.',
  },
  {
    id: 16,
    story:
      '벼락이 탑을 때리고 왕관이 떨어집니다. 오래 쌓아 올린 것이 한순간에 무너지는 장면이에요.',
    upright:
      '갑작스러운 변화나 무너짐을 말합니다. 반갑지 않은 카드지만, 무너지는 것은 대체로 애초에 흔들리고 있던 것이에요. 감춰졌던 문제가 드러나면서 판이 뒤집히고, 그 충격이 오히려 진실을 보게 합니다. 다시 지을 수 있다는 것이 이 카드의 뒷면입니다.',
    reversed:
      '올 것이 미뤄지고 있을 때 나와요. 위기를 가까스로 피했거나, 무너질 것을 알면서 붙들고 있어 불안이 길어지는 상태입니다. 변화가 두려워 손을 못 대고 있을 수 있어요.',
    inLove: '갈등이 터져 나올 수 있습니다. 다만 곪은 것이 드러나는 편이 이어가는 데는 낫습니다.',
    inWork: '계획이 틀어질 수 있어요. 유연하게 다시 짜는 능력이 지금의 실력입니다.',
    advice: '무너진 자리를 원래대로 되돌리려 하지 말고, 다시 설계하세요.',
  },
  {
    id: 17,
    story: '벌거벗은 사람이 물가에서 별빛 아래 물을 붓습니다. 폭풍이 지나간 뒤의 고요한 밤이에요.',
    upright:
      '희망과 회복의 카드입니다. 힘든 시기를 지나온 뒤에 오는 잔잔한 안도, 다시 해볼 수 있겠다는 마음을 말해요. 화려한 성공보다 조용한 치유에 가깝습니다. 지금 품은 바람을 놓지 말라고, 그것이 방향이 되어준다고 일러줍니다. 영감이 잘 떠오르는 시기이기도 해요.',
    reversed:
      '희망을 잃었을 때 나와요. 기대가 꺾여 자신감이 떨어졌거나, 무엇을 바라야 할지 모르는 상태입니다. 회복이 더디게 느껴져 조바심이 나기도 해요.',
    inLove: '상처 뒤의 회복기입니다. 서두르지 않으면 신뢰가 다시 쌓여요.',
    inWork: '장기적인 목표를 다시 세우기 좋은 때입니다. 지금의 노력은 천천히 결실이 됩니다.',
    advice: '크게 바라지 않아도 됩니다. 다만 무엇을 바라는지는 알고 계세요.',
  },
  {
    id: 18,
    story:
      '달빛 아래 두 마리 짐승이 울부짖고, 물에서 무언가 기어 나옵니다. 낮에는 보이지 않던 것들이 드러나는 시간이에요.',
    upright:
      '불안과 혼란을 말하는 카드입니다. 상황이 또렷하지 않아 상상이 사실보다 커지는 시기예요. 지금 보이는 것이 전부가 아닐 수 있으니 중요한 결정은 미루는 편이 좋습니다. 동시에 평소 눌러두었던 감정이 올라오는 때라, 그것을 들여다보면 얻는 것도 있어요.',
    reversed:
      '안개가 걷히는 흐름입니다. 오해가 풀리거나 감춰졌던 사실이 드러나요. 막연했던 두려움의 정체를 알게 되면서 마음이 가벼워지기도 합니다.',
    inLove: '확실하지 않은 말들 때문에 불안해지기 쉬워요. 짐작 대신 직접 물어보세요.',
    inWork: '정보가 부족한 상태입니다. 확인되지 않은 이야기로 판단하지 마세요.',
    advice: '지금 느끼는 두려움 중 사실로 확인된 것이 몇 개인지 세어보세요.',
  },
  {
    id: 19,
    story:
      '해가 환하게 뜨고 아이가 흰 말을 타고 있습니다. 숨길 것도, 어두운 구석도 없는 그림이에요.',
    upright:
      '가장 밝은 카드입니다. 일이 잘 풀리고, 마음이 가볍고, 노력한 것이 눈에 보이는 형태로 드러나요. 감추지 않고 있는 그대로 보여줄 때 더 잘 통하는 시기입니다. 성취뿐 아니라 그 과정에서 느끼는 기쁨까지 함께 말하는 카드예요.',
    reversed:
      '빛이 잠시 가려진 상태입니다. 좋은 흐름이 지연되거나, 성과는 났는데 마음이 따라오지 않을 때 나와요. 지나친 낙관으로 중요한 것을 놓치고 있을 수도 있습니다.',
    inLove: '숨김 없는 애정이 오갑니다. 관계를 드러내고 함께 즐기기 좋은 시기예요.',
    inWork: '성과가 인정받습니다. 자신의 결과물을 적극적으로 보여줘도 좋아요.',
    advice: '잘 되는 이유를 기록해 두세요. 다음에 다시 쓸 수 있습니다.',
  },
  {
    id: 20,
    story:
      '천사의 나팔 소리에 사람들이 관에서 일어나 두 팔을 벌립니다. 지나온 것을 전부 놓고 다시 판단받는 자리예요.',
    upright:
      '지난 일을 다시 보고 매듭짓는 시기입니다. 오래 미뤄둔 결정, 연락하지 못한 사람, 정리하지 못한 감정이 떠오를 수 있어요. 과거를 후회하기 위해서가 아니라 이제 어떻게 할지 정하기 위해 돌아보는 것입니다. 부름을 받은 듯한 명확한 깨달음이 오기도 해요.',
    reversed:
      '결정을 계속 미룰 때 나와요. 스스로를 지나치게 몰아세우며 자책하고 있거나, 반대로 반성해야 할 것을 외면하고 있는 상태입니다. 기회가 왔는데 응답하지 못하는 상황일 수도 있어요.',
    inLove: '지난 관계를 정리하거나 다시 마주하게 됩니다. 답을 미뤄둔 일이 있다면 지금이 때예요.',
    inWork: '평가와 재도전의 시기입니다. 과거의 경험이 지금 쓸모를 드러내요.',
    advice: '미뤄둔 답장을 하나 보내세요. 그것부터가 시작입니다.',
  },
  {
    id: 21,
    story:
      '화환 안에서 한 사람이 춤추고, 네 모서리에 네 상징이 자리를 잡습니다. 한 바퀴가 온전히 완성된 그림이에요.',
    upright:
      '하나의 여정이 마무리되는 카드입니다. 바보로 시작한 길이 여기서 완성돼요. 목표를 이루거나, 흩어져 있던 것들이 하나로 맞아떨어지는 시기입니다. 다만 완성은 끝이 아니라 다음 바퀴의 출발점이기도 해서, 이 카드는 성취를 축하하면서 동시에 다음을 묻습니다.',
    reversed:
      '거의 다 왔는데 마무리가 남았을 때 나와요. 마지막 한 걸음을 미루고 있거나, 끝냈다고 생각했지만 정리되지 않은 부분이 남아 있는 상태입니다.',
    inLove: '관계가 안정된 형태로 자리 잡습니다. 함께 다음 단계를 이야기하기 좋은 때예요.',
    inWork: '프로젝트가 완결되고 인정받습니다. 마무리 점검을 소홀히 하지 마세요.',
    advice: '끝냈다면 충분히 기뻐하세요. 다음은 그 다음에 정해도 됩니다.',
  },
] as const;

export const findMajorMeaning = (cardId: number): MajorMeaning | undefined =>
  MAJOR_MEANINGS.find((m) => m.id === cardId);
